import React, { useState, useRef, useEffect } from "react";
import styles from "./ConversationHistory.module.css";
import { CloseIcon, MoreHorizontalIcon, EditNoteIcon, TrashIcon, CheckIcon } from "../common/Icons";
import { useUIStore } from "@store"; // Import via barrel
import useMediaQuery from "../../hooks/useMediaQuery"; // Import the new hook
import Panel from "../common/Panel"; // Import the new Panel component
import { useChatStore } from "@store"; // Import via barrel
import { formatTimestamp } from "../../utils/dateUtils"; // Import formatTimestamp
import { useAuthStore } from "@store"; // Import via barrel
// import { PencilIcon } from '../icons'; // Placeholder for a potential SVG pencil icon component

interface ConversationHistoryProps {
  overlayMode?: "sidebarMobile" | "inlineInSidebar" | null;
  // Deprecated prop removed: showHeaderNewChat
}

const ConversationHistory: React.FC<ConversationHistoryProps> = ({
  overlayMode = null,
}) => {
  // Use the correctly named state and action from the new uiStore
  const isHistoryPanelVisible = useUIStore(
    (state) => state.isHistoryPanelVisibleOnMobile,
  );
  const toggleHistoryPanel = useUIStore(
    (state) => state.toggleHistoryPanelVisibility,
  );

  // Use the hook to check for medium and smaller screens (breakpoint-md was 1023px)
  const isMediumOrSmaller = useMediaQuery("(max-width: 1023px)");

  const {
    chatHistorySessions,
    isLoadingHistorySessions,
    historySessionsError,
    hasMoreSessionSummaries,
    fetchSessionSummaries,
    setSelectedSessionIdForView,
    selectedSessionIdForView, // To highlight the active session
    renameChatSession,
    deleteChatSession,
  } = useChatStore();

  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [menuOpenSessionId, setMenuOpenSessionId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = (event.composedPath?.()[0] || event.target) as Node;
      if (menuRef.current && !menuRef.current.contains(target)) {
        setMenuOpenSessionId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleEditStart = (session: { session_id: string; title?: string | null }) => {
    setEditingSessionId(session.session_id);
    setEditTitle(session.title || "");
    setMenuOpenSessionId(null); // Close menu
  };

  const handleRenameSubmit = async (sessionId: string) => {
    if (!editTitle.trim()) return;
    await renameChatSession(sessionId, editTitle.trim());
    setEditingSessionId(null);
  };

  const handleDeleteClick = (sessionId: string) => {
    // Call confirm synchronously (inside the click event) to maintain trusted gesture
    if (window.confirm("Sigur doriți să ștergeți această conversație?")) {
      setMenuOpenSessionId(null);
      deleteChatSession(sessionId);
    } else {
      setMenuOpenSessionId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, sessionId: string) => {
    if (e.key === "Enter") {
      e.stopPropagation();
      handleRenameSubmit(sessionId);
    } else if (e.key === "Escape") {
      e.stopPropagation();
      setEditingSessionId(null);
    }
  };


  const { apiUrls, userStatus } = useAuthStore(); // Get apiUrls and userStatus
  const initialDataLoaded = useAuthStore((s) => !!s.jwt);
  const loginUrl = apiUrls?.loginUrl;

  useEffect(() => {
    // Fetch initial batch of session summaries only when authenticated data is available
    if (initialDataLoaded && chatHistorySessions.length === 0) {
      fetchSessionSummaries();
    }
  }, [fetchSessionSummaries, chatHistorySessions.length, initialDataLoaded]);

  // No header actions here; these controls live in the main header/sidebar now

  const handleLoadMore = () => {
    fetchSessionSummaries(true); // true for loadMore
  };

  const navigate = useUIStore((s) => s.navigate);

  const handleSelectSession = (sessionId: string) => {
    setSelectedSessionIdForView(sessionId);
    navigate("chat", { sessionId });
    if (isMediumOrSmaller && isHistoryPanelVisible) {
      toggleHistoryPanel(); // Close panel on mobile after selection
    }
  };

  // Placeholder history items
  // const placeholderHistory = [
  //   { id: '1', title: 'Help me draft a GTM Launch Plan for new feature X...', date: 'Last 7 Days' },
  //   { id: '2', title: 'What are the best strategies for improving email open rates?...', date: 'Last 7 Days' },
  //   { id: '3', title: 'Can you explain the AIDA model in marketing? How can I apply it?...', date: 'March' },
  //   { id: '4', title: 'Help me find the best course for advanced SEO techniques...', date: 'March' },
  //   { id: '5', title: 'Draft a PMF Narrative for a B2B SaaS product that does Y...', date: 'February' },
  // ];

  // Determine the class for the panel based on visibility state for mobile
  const panelWrapperClasses = [
    styles.historyPanel, // This class contains the structural, positional, and transition styles
    overlayMode === "sidebarMobile"
      ? styles.historyOverlayForSidebarMobile
      : overlayMode === "inlineInSidebar"
        ? styles.inlineInAppSidebar
        : isHistoryPanelVisible
          ? styles.historyPanelVisibleMobile
          : styles.historyPanelHiddenMobile,
  ].join(" ");

  // If in sidebarMobile overlay mode, we don't need the backdrop div here, as the panel itself will cover everything.
  // The backdrop is primarily for the slide-in effect from the side in fullscreen mobile.
  const showBackdrop =
    isHistoryPanelVisible &&
    isMediumOrSmaller &&
    overlayMode !== "sidebarMobile" &&
    overlayMode !== "inlineInSidebar";

  return (
    <>
      {/* Backdrop overlay - only visible on mobile when panel is open AND not in sidebarMobile overlay mode*/}
      {showBackdrop && (
        <div
          className={styles.backdrop}
          onClick={toggleHistoryPanel} // Use the new action name
          aria-hidden="true"
        />
      )}
      {/* Use Panel component and pass the specific wrapper classes */}
      <Panel className={panelWrapperClasses}>
        <div className={styles.header}>
          <h2 className={styles.title}>Conversații</h2>
          <div className={styles.headerButtons}>
            {/* Close button - only visible on mobile/tablet (styling in CSS) */}
            <button
              onClick={toggleHistoryPanel} // Use the new action name
              className={styles.closeHistoryButton}
              title="Închide panoul de istoric"
              aria-label="Închide panoul de istoric"
            >
              <CloseIcon
                className={styles.closeIcon}
                title="Pictogramă închidere"
              />
            </button>
          </div>
        </div>
        <div className={styles.listContainer}>
          {isLoadingHistorySessions && chatHistorySessions.length === 0 && (
            <p className={styles.loadingMessage}>Se încarcă istoricul...</p>
          )}
          {historySessionsError && (
            <p className={styles.errorMessage}>{historySessionsError}</p>
          )}

          {!isLoadingHistorySessions &&
            !historySessionsError &&
            chatHistorySessions.length === 0 && (
              <>
                {userStatus !== "guest" ? (
                  <p className={styles.emptyHistoryMessage}>
                    Aici va fi istoricul conversațiilor tale. Începe o nouă
                    conversație pentru a o vedea listată aici.
                  </p>
                ) : (
                  <div className={styles.emptyHistoryGuestContainer}>
                    {" "}
                    {/* New container for centering */}
                    <p className={styles.emptyHistoryMessage}>
                      Aici va fi istoricul conversațiilor tale. Asigură-te că
                      ești autentificat pentru a rămâne salvate.
                    </p>
                    {loginUrl && (
                      <button
                        onClick={() => {
                          if (loginUrl) window.location.href = loginUrl;
                        }}
                        className={styles.loginButton}
                      >
                        Autentificare
                      </button>
                    )}
                  </div>
                )}
              </>
            )}

          {chatHistorySessions.map((session) => (
            <div
              key={session.session_id}
              className={`${styles.historyItem} ${selectedSessionIdForView === session.session_id ? styles.historyItemActive : ""} ${menuOpenSessionId === session.session_id ? styles.historyItemMenuOpen : ""}`}
              onClick={() => handleSelectSession(session.session_id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                e.key === "Enter" && handleSelectSession(session.session_id)
              }
            >
              {editingSessionId === session.session_id ? (
                <div className={styles.inlineEditContainer} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className={styles.editInput}
                    autoFocus
                    onKeyDown={(e) => handleKeyDown(e, session.session_id)}
                    onBlur={() => setEditingSessionId(null)}
                  />
                  <div className={styles.editActions}>
                    <button
                      className={`${styles.iconButton} ${styles.saveButton}`}
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent blur on the input
                        handleRenameSubmit(session.session_id);
                      }}
                      title="Salvează"
                    >
                      <CheckIcon className={styles.menuItemIcon} />
                    </button>
                    <button
                      className={`${styles.iconButton} ${styles.cancelButton}`}
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent blur on the input
                        setEditingSessionId(null);
                      }}
                      title="Anulează"
                    >
                      <CloseIcon className={styles.menuItemIcon} />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className={styles.itemContent}>
                    <span className={styles.itemTitle}>
                      {session.title || "Conversație fără titlu"}
                    </span>
                    <span className={styles.itemDate}>
                      {formatTimestamp(session.last_activity_at)}
                    </span>
                  </div>

                  {/* Actions Menu Trigger */}
                  <div className={styles.itemActions} onClick={(e) => e.stopPropagation()}>
                    <button
                      className={styles.moreButton}
                      onClick={() => setMenuOpenSessionId(menuOpenSessionId === session.session_id ? null : session.session_id)}
                      title="Mai multe opțiuni"
                    >
                      <MoreHorizontalIcon className={styles.menuItemIcon} />
                    </button>

                    {menuOpenSessionId === session.session_id && (
                      <div className={styles.menuDropdown} ref={menuRef}>
                        <button
                          className={styles.menuItem}
                          onClick={() => handleEditStart(session)}
                        >
                          <EditNoteIcon className={styles.menuItemIcon} />
                          Redenumește
                        </button>
                        <button
                          className={`${styles.menuItem} ${styles.deleteAction}`}
                          onClick={() => handleDeleteClick(session.session_id)}
                        >
                          <TrashIcon className={styles.menuItemIcon} />
                          Șterge
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
        <div className={styles.paginationPlaceholder}>
          {" "}
          {/* This div can be restyled or removed if button handles full width */}
          {hasMoreSessionSummaries && !isLoadingHistorySessions && (
            <button
              onClick={handleLoadMore}
              className={styles.loadMoreButton}
              disabled={isLoadingHistorySessions}
            >
              {isLoadingHistorySessions ? "Se încarcă..." : "Încarcă mai multe"}
            </button>
          )}
        </div>
      </Panel>

      {/* Business Profile Modal removed from history sidebar */}
    </>
  );
};

export default ConversationHistory;
